import React, { useMemo } from 'react'
import styled from 'styled-components'
import { ProductDropFieldsFragment } from 'types/graphql'
import { media } from 'utils/media'
import { LinkButton as OriginalButton } from 'lib/Button/Button'
import { Link } from 'lib/Link'
import { Text } from 'lib/Text'
import { LiveDot } from 'modules/thingdrops/components/LiveDot'
import { RemindMeButton as RemindMeButtonOriginal } from 'modules/thingdrops/components/RemindMeButton'
import { SmallTimer } from 'modules/thingdrops/components/Timer'
import { useCurrentBrand } from './context/CurrentBrandProvider'

const Container = styled.div`
  border: var(--border);
  border-color: var(--neutral-20);
  column-gap: 8px;
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-direction: column;
  margin: 24px 0;
  padding: 32px;
  position: relative;

  ${media.tabletDown} {
    padding: 16px 24px;
  }

  ${media.phoneDown} {
    margin: 24px var(--grid-gap-small);
    z-index: 2;
  }
`

const Header = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  gap: 24px;
`

const HeaderText = styled.div`
  align-items: space-between;
  display: flex;
  flex-direction: column;
`

const Image = styled.img`
  background-color: var(--neutral-20);
  flex: 0 0 auto;
  width: 76px;
  height: 76px;
  object-fit: contain;
`

const Button = styled(OriginalButton)`
  align-self: stretch;
  flex: 1;
`

const RemindMeButton = styled(RemindMeButtonOriginal)`
  align-self: stretch;
  flex: 1;
`

const ButtonContainer = styled.div`
  display: flex;
  flex: 1;
  // Tippy popover includes an empty div on render which needs to flex too
  > div {
    display: flex;
    flex: 1;
  }
`

const LiveRow = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
`

interface Props {
  drop: ProductDropFieldsFragment
}

export const ProductDropLiftup = ({ drop }: Props) => {
  const { brand } = useCurrentBrand()

  const time = useMemo(() => {
    if (drop?.dropsAt) {
      return new Date(drop?.dropsAt)
    } else {
      return null
    }
  }, [drop])

  if (!drop || drop.isSoldOut) {
    return null
  }

  return (
    <Container>
      <Header>
        <HeaderText>
          {drop.product && (
            <Text weight="medium" size="s" color="neutral-100" margin="1x">
              {drop.product.name}
            </Text>
          )}
          {drop.hasDropped ? (
            <LiveRow>
              <LiveDot />
              <Text size="pxs" weight="semi" color="neutral-100" shouty>
                Live
              </Text>
            </LiveRow>
          ) : (
            <SmallTimer date={time || undefined} variant="tiny" />
          )}
        </HeaderText>
        {drop.product && <Image alt="" src={drop.product.defaultImageUrl ?? ''} />}
      </Header>

      <Text margin="1x" size="ps" color="neutral-60">
        {drop.hasDropped ? (
          `${brand.title} is now available at a heavily discounted price. Get it while it lasts!`
        ) : (
          <>
            Shop{' '}
            <Link underlineOnNormalState={false} href="/thingdrop">
              Thingdrop
            </Link>{' '}
            this Friday at 11am EST to claim an exclusive to Thingtesting offer on {brand.title}.
          </>
        )}
      </Text>

      {drop.hasDropped ? (
        <Button href={`/thingdrop/${brand.slug}-${drop.id}`}>View drop</Button>
      ) : (
        <ButtonContainer>
          <RemindMeButton brandId={brand.id} brandTitle={brand.title} productDropId={drop.id} canParticipate={drop.canParticipate} />
        </ButtonContainer>
      )}
    </Container>
  )
}
